import React, { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import TextInput from "@scripts/react/components/TextInput";
import Button from "@scripts/react/components/Button";
import { useModel } from "@rapid/contexts/ModelContext";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { StableTextPromptProposalChips } from "../../Prompt/SidebarContent/Annotate/TextPromptProposalChips";
import { IconFrame } from "@scripts/react/components/IconFrame";
import { processTextPromptInputWithAI } from "@rapid/utils/promptParser";
import { SHOW_TEXT_PROMPT_SRC } from "@rapid/constants/media";
import InlineAlert from "@scripts/react/components/InlineAlert";
import { useCurrentMedia } from "@rapid/store";
import { useMedia } from "@rapid/contexts/MediaContext";
import { MediaItemStatus } from "@rapid/types/media";
import { useFlow } from "@rapid/contexts/FlowContext";

interface ObjectPromptCardProps {
    onSubmit: (textPrompts: string) => void;
    initialAnnotationGroup?: string;
}

export const ObjectPromptCard = ({ onSubmit, initialAnnotationGroup }: ObjectPromptCardProps) => {
    const { textPromptStr, setTextPromptStr, textPrompts, updatePromptLabels } = useModel();
    const { mediaItems, orderedMediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const { track } = useAnalytics();
    const { isExtractingWithAI, setIsExtractingWithAI } = useFlow();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<Error | null>(null);
    const [hasEverUploadedFiles, setHasEverUploadedFiles] = React.useState(false);

    const hasFiles = mediaItems.length > 0;

    // Track if files have ever been uploaded
    React.useEffect(() => {
        if (hasFiles && !hasEverUploadedFiles) {
            setHasEverUploadedFiles(true);
        }
    }, [hasFiles, hasEverUploadedFiles]);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        setError(null);

        // Set isExtractingWithAI to show loading states immediately
        setIsExtractingWithAI(true);

        try {
            // Add timeout to prevent infinite loading
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(
                    () => reject(new Error("Please enter objects as a list separated by commas.")),
                    30000
                );
            });

            const result = await Promise.race([
                processTextPromptInputWithAI(textPromptStr),
                timeoutPromise
            ]);

            // Track if objects were extracted from a natural language prompt
            if (result.wasExtracted) {
                track({
                    event: AnalyticsFlowEvent.EXTRACTED_OBJECTS_FROM_PROMPT,
                    data: {
                        originalInput: result.originalInput,
                        extractedObjects: result.extractedObjects,
                        objectCount: result.extractedObjects?.length || 0
                    }
                });
            }

            // Pre-create labels with promptName for translated terms
            if (result.promptNames && result.promptNames.size > 0) {
                const labelsWithPromptNames = Array.from(result.promptNames.entries()).map(
                    ([originalName, englishName]) => ({
                        name: originalName,
                        promptName: englishName
                    })
                );
                updatePromptLabels(labelsWithPromptNames);
            }

            // Use the processed input (extracted objects or original if not extracted)
            // This ensures we only navigate with the actual object classes, not the full prompt
            const processedInput = result.processedInput || textPromptStr;

            // Update the text prompt with the processed result
            setTextPromptStr(processedInput);

            // Navigate with the processed input (extracted objects)
            // advanceToPrompt will set flowLocked before navigation
            onSubmit(processedInput);
        } catch (err) {
            console.error("Failed to process prompt:", err);
            // If extraction fails, still navigate with the original input
            // User can manually edit it on the PROMPT step
            onSubmit(textPromptStr);
        } finally {
            setIsExtractingWithAI(false);
        }
    }, [
        textPromptStr,
        onSubmit,
        track,
        setTextPromptStr,
        updatePromptLabels,
        setIsExtractingWithAI
    ]);

    const hasAnyFiles = orderedMediaItems.length > 0;
    const isLoading = hasAnyFiles && !currentMedia;
    const firstFileInCarousel = orderedMediaItems[0];
    const isFirstFileReady = firstFileInCarousel?.status === MediaItemStatus.COMPLETED;
    const isDisabled = !hasAnyFiles || isLoading || !textPrompts.length || !isFirstFileReady;

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey && !isDisabled && !isSubmitting) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit, isDisabled, isSubmitting]
    );

    // Clear error when user starts typing again
    const handleInputChange = useCallback(
        (value: string) => {
            if (error) {
                setError(null);
            }
            setTextPromptStr(value);
        },
        [error, setTextPromptStr]
    );

    // Initialize text prompt from URL parameter when component mounts or when initialAnnotationGroup becomes available
    const hasInitializedFromUrl = React.useRef(false);
    useEffect(() => {
        if (hasInitializedFromUrl.current) return;
        hasInitializedFromUrl.current = true;

        // Don't overwrite values set by example videos
        if (initialAnnotationGroup && !textPromptStr) {
            setTextPromptStr(initialAnnotationGroup);
        }
    }, [initialAnnotationGroup, textPromptStr, setTextPromptStr]);

    // Don't render until files have been uploaded at least once
    if (!hasEverUploadedFiles) {
        return null;
    }

    return (
        <div className="fileUploadContainer flex h-full w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
                className="fileUploadMethod w-full !items-stretch gap-6"
            >
                {/* Header with icon */}
                <div className="fileUploadMethodContent">
                    <IconFrame
                        icon="far fa-search"
                        size="lg"
                        isCircle={true}
                        customColor="bg-tint text-brand"
                    />
                    <div className="flex flex-col gap-0.5 text-center">
                        <h2 className="text-primary">What objects are you looking for?</h2>
                        {SHOW_TEXT_PROMPT_SRC && (
                            <p className="text-sm text-tertiary">
                                Powered by{" "}
                                <a
                                    href="https://ai.meta.com/sam3/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    SAM3
                                </a>
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-1 flex-col justify-between gap-2 text-base text-gray-700">
                    <div className="flex flex-col gap-2">
                        <TextInput
                            placeholder="Enter objects: person, card, dog..."
                            value={textPromptStr}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            fullWidth={true}
                            autoFocus={true}
                            disabled={isSubmitting}
                        />
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            disabled={isDisabled || isSubmitting}
                            loading={
                                hasAnyFiles &&
                                (isLoading ||
                                    isSubmitting ||
                                    isExtractingWithAI ||
                                    !isFirstFileReady)
                            }
                            loadingText={
                                isSubmitting || isExtractingWithAI
                                    ? "Finding Objects"
                                    : "Processing Files"
                            }
                        >
                            {!hasAnyFiles
                                ? "Upload a File"
                                : isSubmitting
                                  ? "Finding Objects"
                                  : "Find My Objects"}
                            <i className="far fa-arrow-right" />
                        </Button>
                    </div>

                    <AnimatePresence mode="wait">
                        {error ? (
                            <motion.div
                                key="extract-objects-error"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                            >
                                <InlineAlert
                                    level="danger"
                                    type="secondary"
                                    icon="fa fa-exclamation-triangle"
                                    dense={true}
                                >
                                    {error.message}
                                </InlineAlert>
                            </motion.div>
                        ) : (
                            !!currentMedia &&
                            !isSubmitting && (
                                <motion.div
                                    key="chips-container"
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 5 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <StableTextPromptProposalChips
                                        isDisabled={false}
                                        showPoweredBy={false}
                                    />
                                </motion.div>
                            )
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};
